/**
 * Pitch auto-calibration geometry — the TypeScript port of the research chain's
 * `experiments/pitch_geometry.py` (exp24 / "PitchNet"). Ported 1:1 so the app
 * reproduces the model's expected input frame and decodes its output exactly.
 *
 * Coordinate convention (matches the trained model — do NOT swap):
 *   x = easting  (from longitude, scaled by cos(lat))
 *   y = northing (from latitude)
 * PCA rotates the GPS cloud to canonical orientation; the model's box is decoded
 * back to lat/lng through the inverse of that same rotation.
 *
 * The PCA rotation is LOAD-BEARING: a flipped principal-axis sign moves the
 * decoded pitch corners by 60-120 m (the model is not reflection-invariant). The
 * Python reference gets its sign from `numpy.linalg.eigh`; this port reproduces
 * that exact sign in closed form via `principalAxis()` below (verified to match
 * numpy to machine precision on real tracks and over 200k random 2x2 matrices).
 */

const METRES_PER_DEG = 111000.0;

export interface Pca {
    clat: number;
    clng: number;
    cosLat: number;
    /** Rows are the principal axes; det(rot) === +1 (a proper rotation). */
    rot: [[number, number], [number, number]];
    /** sqrt(lambda_major / lambda_minor) — track elongation. */
    spread: number;
    /** Heuristic: an assistant-referee (touchline) track runs very elongated. */
    isLineRef: boolean;
}

export interface DecodedCalibration {
    left: [number, number]; // [lat, lng]
    right: [number, number];
    mid: [number, number];
}

/**
 * Principal eigenvector of the symmetric 2x2 `[[a, b], [b, c]]`, with the SAME
 * sign convention `numpy.linalg.eigh` uses (so the point cloud we feed the model
 * matches the frame it was trained in). Reverse-engineered and verified exact:
 *   theta = 0.5 * atan2(2b, a - c);  pa = (cos theta, sin theta)
 *   numpy returns  pa  iff (b >= 0 and a < c),  else  -pa.
 * det(rot) is always +1, so the minor axis is forced to (-pa.y, pa.x).
 */
function principalAxis(a: number, b: number, c: number): [number, number] {
    const theta = 0.5 * Math.atan2(2 * b, a - c);
    let x = Math.cos(theta);
    let y = Math.sin(theta);
    if (!(b >= 0 && a < c)) {
        x = -x;
        y = -y;
    }
    return [x, y];
}

/**
 * PCA of the GPS cloud in a local metre frame centred on the track mean.
 * Port of `pitch_geometry.compute_pca`. The eigenvectors are scale-invariant, so
 * the covariance is left unnormalised (no ddof factor) — identical rotation.
 */
export function computePca(lats: number[], lngs: number[]): Pca {
    const n = lats.length;
    let clat = 0;
    let clng = 0;
    for (let i = 0; i < n; i++) {
        clat += lats[i];
        clng += lngs[i];
    }
    clat /= n;
    clng /= n;
    const cosLat = Math.cos((clat * Math.PI) / 180);

    // Unnormalised covariance of the centred metre-space coords.
    let sxx = 0;
    let sxy = 0;
    let syy = 0;
    for (let i = 0; i < n; i++) {
        const dx = (lngs[i] - clng) * METRES_PER_DEG * cosLat;
        const dy = (lats[i] - clat) * METRES_PER_DEG;
        sxx += dx * dx;
        sxy += dx * dy;
        syy += dy * dy;
    }

    const [px, py] = principalAxis(sxx, sxy, syy);
    // rot rows = [major axis, minor axis]; det = +1.
    const rot: [[number, number], [number, number]] = [
        [px, py],
        [-py, px],
    ];

    // Eigenvalues of [[sxx, sxy], [sxy, syy]] for the spread ratio.
    const tr = sxx + syy;
    const disc = Math.sqrt(Math.max(0, ((sxx - syy) / 2) ** 2 + sxy * sxy));
    const lamMajor = tr / 2 + disc;
    const lamMinor = tr / 2 - disc;
    const spread = Math.sqrt(lamMajor / Math.max(lamMinor, 1e-6));

    return { clat, clng, cosLat, rot, spread, isLineRef: spread > 5.0 };
}

/** A single lat/lng into the PCA metre frame. */
function latLngToPcaM(lat: number, lng: number, pca: Pca): [number, number] {
    const dx = (lng - pca.clng) * METRES_PER_DEG * pca.cosLat;
    const dy = (lat - pca.clat) * METRES_PER_DEG;
    // [dx, dy] @ rot.T  ->  (dx*rot[k][0] + dy*rot[k][1]) for k in {0,1}
    return [
        dx * pca.rot[0][0] + dy * pca.rot[0][1],
        dx * pca.rot[1][0] + dy * pca.rot[1][1],
    ];
}

/** A point in the PCA metre frame back to [lat, lng]. */
function pcaMToLatLng(xy: [number, number], pca: Pca): [number, number] {
    // inv = rot^-1; dxy = xy @ inv.T. rot is a proper rotation (det +1), so
    // inv = [[ r11, -r01], [-r10, r00]] (standard 2x2 inverse, det = 1).
    const r = pca.rot;
    const inv: [[number, number], [number, number]] = [
        [r[1][1], -r[0][1]],
        [-r[1][0], r[0][0]],
    ];
    const dx = xy[0] * inv[0][0] + xy[1] * inv[0][1];
    const dy = xy[0] * inv[1][0] + xy[1] * inv[1][1];
    const lat = pca.clat + dy / METRES_PER_DEG;
    const lng = pca.clng + dx / (METRES_PER_DEG * pca.cosLat);
    return [lat, lng];
}

/**
 * numpy `linspace(0, stop, num).astype(int)` — evenly spaced indices, truncated
 * toward zero, with the last element pinned exactly to `stop` (as numpy does
 * before the int cast). Reproduced exactly so the subsampling matches the golden.
 */
function linspaceInt(stop: number, num: number): number[] {
    const step = stop / (num - 1);
    const out: number[] = Array.from({ length: num });
    for (let i = 0; i < num; i++) {
        out[i] = Math.trunc(i * step);
    }
    out[num - 1] = Math.trunc(stop);
    return out;
}

/**
 * Build the model input point cloud in the PCA metre frame.
 * Port of `pitch_geometry.prepare_point_cloud` then the §11.1 subsample:
 *   coords -> 1024-point cloud -> 512 points at linspace(0, 1023, 512).
 *
 * For n >= nPoints the cloud is an even linspace subsample (the deterministic
 * regime that ~96% of real tracks hit). For n < nPoints the Python reference
 * pads with RANDOM resampling; here we pad deterministically by wrap-around
 * repetition instead — the PointNet max-pool is insensitive to duplicate
 * density, and a deterministic result is required for a reproducible app.
 *
 * Returns a Float32Array of length 512*2 (row-major [x0,y0,x1,y1,...]) ready for
 * the ONNX tensor, or null if there are too few points (< 50).
 */
export function preparePointCloud(
    lats: number[],
    lngs: number[],
    pca: Pca,
    nPoints = 1024
): Float32Array | null {
    const n = lats.length;
    if (n < 50) {
        return null;
    }

    // Full-resolution coords in the PCA frame.
    const cx = new Float64Array(n);
    const cy = new Float64Array(n);
    for (let i = 0; i < n; i++) {
        const m = latLngToPcaM(lats[i], lngs[i], pca);
        cx[i] = m[0];
        cy[i] = m[1];
    }

    // 1024-point cloud.
    const cloudX = new Float64Array(nPoints);
    const cloudY = new Float64Array(nPoints);
    if (n >= nPoints) {
        const idx = linspaceInt(n - 1, nPoints);
        for (let i = 0; i < nPoints; i++) {
            cloudX[i] = cx[idx[i]];
            cloudY[i] = cy[idx[i]];
        }
    } else {
        for (let i = 0; i < n; i++) {
            cloudX[i] = cx[i];
            cloudY[i] = cy[i];
        }
        // Deterministic wrap-around padding (see doc comment above).
        for (let i = n; i < nPoints; i++) {
            cloudX[i] = cx[i % n];
            cloudY[i] = cy[i % n];
        }
    }

    // Subsample 1024 -> 512 (§11.1: idx = linspace(0, 1023, 512)).
    const half = 512;
    const sub = linspaceInt(nPoints - 1, half);
    const out = new Float32Array(half * 2);
    for (let j = 0; j < half; j++) {
        out[j * 2] = cloudX[sub[j]];
        out[j * 2 + 1] = cloudY[sub[j]];
    }
    return out;
}

/**
 * De-standardise the model's first four outputs in place-safe fashion.
 * `out[:4] = out[:4] * std4 + mean4` (meta.json). Returns a new array.
 */
export function destandardise(
    raw: ArrayLike<number>,
    mean4: number[],
    std4: number[]
): number[] {
    const out: number[] = Array.from({ length: raw.length });
    for (let i = 0; i < raw.length; i++) {
        out[i] = i < 4 ? raw[i] * std4[i] + mean4[i] : raw[i];
    }
    return out;
}

/**
 * Decode de-standardised box params `[cx, cy, log_half_L, log_half_W, sin2t,
 * cos2t]` (metre frame) into {left, right, mid} lat/lng. Port of
 * `pitch_geometry.box_params_to_calibration` (flip=false).
 */
export function boxParamsToCalibration(
    params: number[],
    pca: Pca
): DecodedCalibration {
    const cx = params[0];
    const cy = params[1];
    const halfW = Math.exp(params[2]);
    const halfH = Math.exp(params[3]);
    const theta = Math.atan2(params[4], params[5]) / 2.0;
    const cosT = Math.cos(theta);
    const sinT = Math.sin(theta);
    // perp = (-sinT, cosT); lrDir = (cosT, sinT)
    const lrMidX = cx + halfH * -sinT;
    const lrMidY = cy + halfH * cosT;
    return {
        left: pcaMToLatLng([lrMidX - halfW * cosT, lrMidY - halfW * sinT], pca),
        right: pcaMToLatLng(
            [lrMidX + halfW * cosT, lrMidY + halfW * sinT],
            pca
        ),
        mid: pcaMToLatLng([cx, cy], pca),
    };
}
